import { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { TextField } from '@habitech/keychain-atoms-inputs';
import { Toggle } from '@habitech/selectors';
import { Button } from '@habitech/call-to-action';
import { postScoringPrograms, putScoringPrograms } from 'src/apis/scoringPrograms';
import Modal from 'src/components/common/modal/Modal';
import Loading from 'src/components/common/Loading/Loading';
import { WrapperButton, TextModal } from '../styles';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/RootState';

export const typeForm = {
    create: 'Crear',
    edit: 'Editar',
};

type PropsForm = {
    type: 'Crear' | 'Editar';
    initialValues: {};
    onCloseModal?: Function;
    setLoadingList?: Function;
};

const initStateModal = {
    show: false,
    text: '',
    success: false,
};
const Form = ({ type, initialValues, setLoadingList, onCloseModal }: PropsForm) => {
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(initStateModal);
    const [deleting, setDeleting] = useState(false);
    const token = useSelector((state: RootState) => state.User.token);
    const validationSchema = yup.object({
        name: yup.string().required('Debe escribir un nombre'),
        description: yup.string().required('Debe escribir una descripción'),
        number_score: yup.number().required('Digita el número de puntos').min(-400, 'Debe ser mayor a -400 puntos').max(400, 'Debe ser menor o igual a 400 puntos'),
        days: yup.number().required('Digita el número de días').min(1, 'Debe ser mayor a 1 día').max(365000, 'Debe ser menor a un milenio'),
    });

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit: (values: any, formikHelpers) => {
            setLoading(true);
            if (type === typeForm.create) {
                postScoringPrograms(values, token)
                    .then(() => {
                        setModal({
                            show: true,
                            text: 'Se creo el nuevo programa con exito',
                            success: true,
                        });
                        setLoading(false);
                        formikHelpers.resetForm();
                    })
                    .catch((e) => {
                        console.error(e);
                        setModal({
                            show: true,
                            text: 'Hubo un error al crear el programa',
                            success: false,
                        });
                        setLoading(false);
                    });
            }
            if (type === typeForm.edit) {
                const payload = deleting ? { ...values, is_active: -1 } : values;
                putScoringPrograms(payload, token)
                    .then(() => {
                        setModal({
                            show: true,
                            text: deleting ? 'Programa eliminado con éxito' : 'Se edito el programa con exito',
                            success: true,
                        });
                        setLoading(false);
                    })
                    .catch((e) => {
                        console.error(e);
                        setModal({
                            show: true,
                            text: deleting ? 'Hubo un error al eliminar el programa' : 'Hubo un error al editar el programa',
                            success: false,
                        });
                        setLoading(false);
                    });
            }
        },
    });

    const handleClose = () => {
        setModal(initStateModal);
        if (modal.success) {
            setLoadingList(true);
        }
        if (onCloseModal) {
            onCloseModal(false);
        }
        setDeleting(false);
    };

    const omitSignPlus = (e) => {
        if (e.key === '+') {
            e.preventDefault()
        }
    }

    const omitNumberSigns = (e) => {
        if (e.key === '-' || e.key === '+') {
            e.preventDefault()
        }
    }

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <TextField
                    dataId="name-program"
                    name="name"
                    label="Nombre del programa"
                    originType="pcom"
                    placeHolder="Digite el nombre del programa"
                    innerLabel
                    value={formik.values.name}
                    onChange={(_, _value) => formik.setFieldValue('name', _value, false)}
                    error={Boolean(formik.errors.name)}
                    errorMessage={`${formik.errors.name}`}
                />
                <TextField
                    dataId="description-program"
                    name="description"
                    label="Descripción del programa"
                    originType="pcom"
                    placeHolder="Digite la descripción del programa"
                    innerLabel
                    value={formik.values.description}
                    onChange={(_, value) => formik.setFieldValue('description', value, false)}
                    error={Boolean(formik.errors.description)}
                    errorMessage={`${formik.errors.description}`}
                />
                <TextField
                    dataId="number-program"
                    name="number_score"
                    label="Puntos (de -400 a 400)"
                    originType="pcom"
                    placeHolder="Digite el número de puntos"
                    innerLabel
                    type="number"
                    value={formik.values.number_score}
                    onChange={(_, value) => formik.setFieldValue('number_score', value, false)}
                    error={Boolean(formik.errors.number_score)}
                    errorMessage={`${formik.errors.number_score}`}
                    onWheel={(e) => e.preventDefault()}
                    onKeyDown={(e) => omitSignPlus(e)}
                />
                <TextField
                    dataId="days-program"
                    name="days"
                    label="Días de vigencia"
                    originType="pcom"
                    placeHolder="Digite el número de días"
                    innerLabel
                    type="number"
                    minLength={2}
                    value={formik.values.days}
                    onChange={(_, value) => formik.setFieldValue('days', value, false)}
                    error={Boolean(formik.errors.days)}
                    errorMessage={`${formik.errors.days}`}
                    onWheel={(e) => e.preventDefault()}
                    onKeyDown={(e) => omitNumberSigns(e)}
                />
                <div className="toggle" style={deleting ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
                    <p>¿Activo?</p>
                    <Toggle
                        dataId="is_active"
                        name={`is_active-${type}`}
                        onToggle={(_, toogle) => formik.setFieldValue('is_active', toogle ? 1 : 0, false)}
                        originType="pcom"
                        value={formik.values.is_active}
                        showLabel
                        selected={formik.values.is_active === 1}
                    />
                </div>
                <WrapperButton>
                    {!loading ? (<>
                        {onCloseModal ? (
                            <Button dataId="button-search" originType="pcom" variant="white" size="large" onClick={handleClose}>
                                cancelar
                            </Button>
                        ) : (
                            ''
                        )}
                        {type === typeForm.edit ? (
                            <button
                                data-id="button-delete"
                                className={deleting ? 'warning-btn' : 'danger-btn'}
                                type="button"
                                onClick={() => setDeleting((prev) => !prev)}
                            >
                                {deleting ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        ) : (
                            ''
                        )}
                        <Button dataId="button-search" originType="pcom" size="large" type="submit">
                            {type} programa
                        </Button>
                    </>

                    ) : (
                        <Loading className="loader-button" />
                    )}
                </WrapperButton>
            </form>
            {modal.show && (
                <Modal onClose={handleClose}>
                    <div style={{ margin: '48px 0 24px' }}>
                        <TextModal>{modal.text}</TextModal>
                        <WrapperButton>
                            <Button dataId="button-search" originType="pcom" size="large" onClick={handleClose}>
                                Aceptar
                            </Button>
                        </WrapperButton>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default Form;
